import { useEffect, useRef, useState } from "react";
import { Link as RemixLink } from "@remix-run/react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";

import { useLocale } from "#app/context/LocaleContext.jsx";
import { LANDING_STATS } from "#app/config/landingStats.js";
import { pickEmoji, useOpeningData } from "./openingUtils.js";

const blink = keyframes`50% { opacity: 0.35; }`;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Count-up that fires once, when the element scrolls into view. SSR and the
// first client render both show `target` (so there's no hydration mismatch and
// no-JS / reduced-motion users see the real number immediately); the effect
// then rewinds to 0 and animates up. Returns [displayValue, ref-to-observe].
function useCountUp(target, duration = 1800) {
  const ref = useRef(null);
  const [value, setValue] = useState(target);

  useEffect(() => {
    const node = ref.current;
    if (!node || target == null) return;

    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (prefersReduced) {
      setValue(target);
      return;
    }

    setValue(0);
    let raf;
    let started = false;

    const animate = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        setValue(Math.round(easeOutCubic(t) * target));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started) {
            started = true;
            animate();
            observer.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return [value, ref];
}

// One real alert, rendered the way it actually lands on a phone — the SAME copy
// we send ("<Facility> has a new opening", then "date · site · nights"), just
// free-standing instead of inside a phone frame. Tight and authentic: no
// invented subject lines, no "book before it's gone" flourish. These are the
// evidence behind the headline number, so they must read as the genuine alert.
function AlertCard({ opening, locale, highlight }) {
  const { dateLabel, timeAgo, siteName, nights } = useOpeningData(
    opening,
    locale,
  );
  const emoji = pickEmoji(opening.facilityName + opening.availableDate);
  const title = `${opening.facilityName} has a new opening ${emoji}`;
  const body = [dateLabel, siteName, nights].filter(Boolean).join(" · ");

  return (
    <HStack
      spacing={2.5}
      align="start"
      w="100%"
      bg="bg-surface"
      borderWidth="1px"
      borderColor="border"
      borderRadius="xl"
      boxShadow={highlight ? "md" : "sm"}
      px={3.5}
      py={2.5}
    >
      <Image
        src="/apple-touch-icon-v2.png"
        alt="Schnerp"
        boxSize="30px"
        borderRadius="8px"
        flexShrink={0}
        mt={0.5}
      />
      <Box flex={1} minW={0}>
        <Flex justify="space-between" align="center" gap={2}>
          <Text fontSize="2xs" fontWeight="semibold" color="subtle">
            Schnerp
          </Text>
          <Text
            fontSize="2xs"
            color="subtle"
            flexShrink={0}
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {timeAgo}
          </Text>
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color="emphasized"
          lineHeight="short"
          noOfLines={1}
        >
          {title}
        </Text>
        {body && (
          <Text fontSize="xs" color="muted" lineHeight="short" noOfLines={1}>
            {body}
          </Text>
        )}
      </Box>
    </HStack>
  );
}

/**
 * The "does it actually work?" proof — framed as validation. The alerts-sent
 * track record (since 2022) is the hero claim, rendered as a live count-up that
 * lands on the real number "and counting" — quiet confidence that we've done
 * this a long time. The feed of real alerts is the evidence the number is
 * genuine: every row is a real opening, in the exact copy we send.
 *
 * Light/clean in both color modes via semantic tokens. Homepage-only.
 */
export function NotificationProof({
  openings = [],
  alertsSent,
  ctaTo = "/new",
}) {
  const locale = useLocale();
  const siteCount = LANDING_STATS.totalSiteCount;
  // Real "alerts delivered" count from the loader. Null when the loader skipped
  // the DB (HEAD requests) or the count failed — we omit the figure rather than
  // show a hardcoded placeholder.
  const target = alertsSent;
  const [count, countRef] = useCountUp(target);
  const feed = openings.slice(0, 5);

  // Rendered in two slots: between the number and the bullets on mobile (so the
  // section's CTA isn't stacked right under How-it-works' CTA), and as its own
  // column on desktop. Only one copy is visible per breakpoint (display toggle).
  const feedContent =
    feed.length > 0 ? (
      <VStack spacing={2.5} w="100%">
        {feed.map((opening, i) => (
          <AlertCard
            key={`${opening.facilityName}-${opening.availableAt}-${i}`}
            opening={opening}
            locale={locale}
            highlight={i === 0}
          />
        ))}
      </VStack>
    ) : (
      <Box
        bg="bg-surface"
        borderWidth="1px"
        borderColor="border"
        borderRadius="xl"
        px={4}
        py={8}
        textAlign="center"
      >
        <Text fontSize="sm" color="muted">
          Watching {siteCount.toLocaleString(locale)}+ sites for the next
          opening&hellip;
        </Text>
      </Box>
    );

  return (
    <Box
      as="section"
      w="100%"
      maxW="container.lg"
      mx="auto"
      px={{ base: 4, md: 6 }}
    >
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 8, md: 14 }}
        alignItems="center"
      >
        {/* Left — the validation. The number leads; everything else supports. */}
        <VStack align="start" spacing={6}>
          <HStack spacing={2}>
            <Box
              boxSize="9px"
              borderRadius="full"
              bg="green.400"
              boxShadow="0 0 0 4px rgba(72,196,64,0.18)"
              sx={{ animation: `${blink} 1.6s steps(1) infinite` }}
            />
            <Text
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="success"
            >
              Watching now
            </Text>
          </HStack>

          {target != null && (
            <Box>
              <Heading
                as="h2"
                ref={countRef}
                // Matches the hero H1's scale (4xl→5xl) so the stat reads as a
                // top-tier focal point without out-sizing the page headline.
                fontSize={{ base: "4xl", md: "5xl" }}
                fontWeight="extrabold"
                letterSpacing="tight"
                lineHeight="1"
                color="emphasized"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {count.toLocaleString(locale)}
              </Heading>
              <Text
                mt={3}
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="semibold"
                color="muted"
              >
                real alerts sent since 2022
                <Text as="span" fontWeight="bold">
                  {" "}
                  &middot; and counting
                </Text>
              </Text>
            </Box>
          )}

          <Text fontSize={{ base: "md", md: "lg" }} color="muted">
            Real openings, sent to real campers every day, all season long. Here
            are a few of the latest:
          </Text>

          {/* Feed — mobile only, slotted between the narrative and the CTA. */}
          <Box display={{ base: "block", md: "none" }} w="100%">
            {feedContent}
          </Box>

          <Button
            variant="primary"
            size="lg"
            fontWeight="bold"
            as={RemixLink}
            to={ctaTo}
            rightIcon={<ArrowForwardIcon />}
          >
            Start watching for free
          </Button>
        </VStack>

        {/* Right — the evidence (desktop only; mobile renders it above). */}
        <Box display={{ base: "none", md: "block" }} w="100%">
          {feedContent}
        </Box>
      </SimpleGrid>
    </Box>
  );
}
